import { ButtonPrimary, ButtonStatus } from "@nova/component-btn"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import {
    AGOLGroup,
    AGOLShareOptions,
    AGOLWebMap,
    AuthorizationType,
} from "@nova/db-table-integration-authorizations"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { useUpdateIntegrationAuthorization } from "features/data-management/integrations/useIntegrations"
import { useAppDispatch } from "integrations/redux/hooks"
import type { IntegrationAuthorizationResponse } from "@nova/data-facade"
import { FunctionComponent, useState } from "react"
import useSWR from "swr"
import { Spinner } from "themes/default-tailwind"
import { setGlobalNotificationContext } from "user/profile/slice"
import { Select } from "@nova/component-select"
import { IntegrationType } from "@nova/schemas"

interface AGOLIntegrationUIProps {
    authorization: IntegrationAuthorizationResponse<AuthorizationType, IntegrationType.agol>
}

const userContentOption = { title: "User Content" } as unknown as AGOLWebMap

const getSharingOptions = (groups: Array<AGOLGroup>) =>
    [
        groups && {
            title: "Groups",
            scope: "group",
            groups: groups,
        },
        { title: "Private" },
        { title: "Public", scope: "public" },
        { title: "Organization", scope: "org" },
    ].filter((option) => !!option) as Array<AGOLShareOptions & { title: string }>

type SharingOption = ReturnType<typeof getSharingOptions>[number]

const isSharingTheSame = (
    sharing: AGOLShareOptions | undefined,
    sharingSettings: AGOLShareOptions | undefined
) => {
    if (!sharing) return true

    if (sharing.scope !== sharingSettings?.scope) return false

    if (sharingSettings?.scope !== "group" || sharing.scope !== "group") return true

    if (sharingSettings.groups.length !== sharing.groups.length) return false

    return sharingSettings?.groups.every((group) => sharing.groups.find((sg) => group.id === sg.id))
}

const AGOLIntegrationUI: FunctionComponent<AGOLIntegrationUIProps> = ({ authorization }) => {
    const [webMap, setWebMap] = useState<AGOLWebMap>()
    const { trigger: updateIntegrationAuthorization, isMutating } =
        useUpdateIntegrationAuthorization()
    const agolSettings = authorization.settings
    const dispatch = useAppDispatch()

    const {
        data: webMaps,
        isLoading: isWebMapsLoading,
        error,
    } = useAGOLWebMaps(authorization.workspaceId, {
        doOnError: () => {
            dispatch(
                setGlobalNotificationContext({
                    message: `Failed to retrieve project list from AGOL. Please try again later.`,
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 3000,
                })
            )
            console.error(error)
        },
    })

    const { data: groups, isLoading: isGroupsLoading } = useAGOLGroups(authorization.workspaceId)

    const webMapOptions = [...(webMaps ?? []), userContentOption]
    const baseSharingOptions = getSharingOptions(groups ?? [])

    const defaultWebMap = webMaps?.find((proj) => proj.id === agolSettings?.selectedWebMap?.id)

    const matchingSharingOption: SharingOption | undefined = baseSharingOptions?.find(
        (option) => option.scope === agolSettings.sharing?.scope
    )
    const defaultSharing: SharingOption | undefined = matchingSharingOption && {
        title: matchingSharingOption.title,
        scope: matchingSharingOption.scope,
        groups: (agolSettings.sharing?.scope === "group" && agolSettings.sharing.groups) || [],
    }
    const [sharing, setSharing] = useState<SharingOption | undefined>(defaultSharing)

    const isWebMapUnchanged = defaultWebMap?.id === webMap?.id || !webMap
    const isSharingUnchanged = isSharingTheSame(sharing, agolSettings.sharing)

    if (error) {
        return <></>
    }
    if (isWebMapsLoading || isGroupsLoading || !groups) {
        return <Spinner />
    }

    const getSharingTitle = (value: SharingOption | undefined) => {
        const realValue = value ?? defaultSharing

        if (realValue?.scope === "group") {
            if (!realValue.groups.length) return "No groups"
            if (realValue.groups.length === 1) return "1 group"
            return `${realValue.groups.length} groups`
        }

        return realValue?.title ?? "Select..."
    }

    const sharingOptions = baseSharingOptions.map((option) => ({
        label: option.title,
        value: option,
        multiSubConfig:
            option.scope === "group"
                ? {
                      options: option.groups.map((v) => ({
                          label: v.title,
                          value: v,
                      })),
                      selected:
                          (sharing?.scope === "group" &&
                              sharing?.groups?.map((v) => ({
                                  label: v.title,
                                  value: v,
                              }))) ||
                          [],
                      onChange: (value: AGOLGroup) => {
                          setSharing((prev) => {
                              if (!value) return prev
                              if (prev?.scope !== option.scope) {
                                  return {
                                      ...option,
                                      groups: option.groups.filter((v) => v.id === value.id),
                                  }
                              }
                              if (prev.groups.map((s) => s.id).includes(value.id)) {
                                  return {
                                      ...prev,
                                      groups: prev.groups.filter((s) => s.id !== value.id),
                                  }
                              }
                              return {
                                  ...prev,
                                  groups: [...(prev.groups ?? []), value],
                              } as SharingOption
                          })
                      },
                  }
                : undefined,
    }))

    const handleSubmit = () => {
        let newWebMap = undefined
        if (webMap === userContentOption) newWebMap = null
        if (webMap)
            newWebMap = {
                id: webMap.id,
                owner: webMap.owner,
                title: webMap.title,
            }

        let newSharing: AGOLShareOptions | undefined | null = undefined
        if (sharing && !sharing.scope) newSharing = null
        if (sharing)
            newSharing = {
                scope: sharing.scope,
                groups: sharing.scope === "group" ? sharing.groups : undefined,
            } as AGOLShareOptions

        updateIntegrationAuthorization({
            workspaceId: authorization.workspaceId,
            id: authorization.id,
            authUserId: authorization.userId,
            fields: {
                settings: {
                    selectedWebMap: newWebMap,
                    sharing: newSharing,
                },
            },
        }).then(() => {
            dispatch(
                setGlobalNotificationContext({
                    message: `Your destination has been updated.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 3000,
                })
            )
        })
    }

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h5 className="text-gray-100">Sharing Level</h5>
                <div className="w-60">
                    <Select<SharingOption | undefined, AGOLGroup>
                        onChange={(value) => setSharing(value)}
                        defaultValue={defaultSharing}
                        getTitle={getSharingTitle}
                        options={sharingOptions}
                        value={sharing ?? defaultSharing}
                        getOptionValue={(o) => o?.scope}
                    />
                </div>
            </div>
            <div>
                <h5 className="text-gray-100">Destination</h5>
                <div className="flex gap-2">
                    <div className="w-60">
                        <Select<AGOLWebMap | undefined>
                            onChange={(value) => setWebMap(value)}
                            options={webMapOptions.map((project) => ({
                                label: project.title,
                                value: project,
                            }))}
                            value={webMap}
                            defaultValue={defaultWebMap}
                            getOptionValue={(o) => o?.id}
                        />
                    </div>
                </div>
            </div>

            <ButtonPrimary
                onClick={handleSubmit}
                status={isMutating ? ButtonStatus.Loading : ButtonStatus.Normal}
                disabled={isWebMapUnchanged && isSharingUnchanged}
                title="Update"
                height="h-10"
                className="w-fit"
            />
        </div>
    )
}

const useAGOLWebMaps = (workspaceId: string, options: { doOnError?: () => void } = {}) => {
    const { data, isLoading, error } = useSWR(
        ["agol-web-maps", workspaceId],
        async () =>
            (await fetchApiGateway("get /org/:orgId/integrations/agol/projects", {
                params: { orgId: workspaceId },
            })) as AGOLWebMap[],
        {
            revalidateOnFocus: false,
            shouldRetryOnError: false,
            onError: options.doOnError,
        }
    )

    return { data, isLoading, error }
}

const useAGOLGroups = (workspaceId: string) => {
    const { data, isLoading, error } = useSWR(
        ["agol-groups", workspaceId],
        async () =>
            (await fetchApiGateway("get /org/:orgId/integrations/agol/groups", {
                params: { orgId: workspaceId },
            })) as AGOLGroup[],
        {
            revalidateOnFocus: false,
            shouldRetryOnError: false,
        }
    )

    return { data, isLoading, error }
}

export { AGOLIntegrationUI }
